import type { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { AddNewPolicyBodyRequest } from '@/domains/policies/types/api/requests/AddPolicyBodyRequest';
import { DeletePolicyBodyRequest } from '../types/api/requests/DeletePolicyBodyRequest';
import { LinkPolicyToClaimRequest } from '../types/api/requests/LinkClaimBodyRequest';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import { getClaimSetV2 } from '@/domains/claimSets/store/claimSetSlice';
import { getBenefitSectionDetails } from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimList';
import PolicyEvents from '@/domains/policies/events';
import { FAILED_TO_SAVE } from '@/constants/texts';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import SnacksService from '@/services/SnackService/SnackService';
import {
  addNewPolicy,
  addNewPolicySuccess,
  deletePolicy,
  getClaimOptions,
  getClaimOptionsSuccess,
  getCreatePolicyOptionsSuccess,
  getPolicyStatuses,
  getPotentialPoliciesFilteredList,
  linkPolicyToClaim,
  setClaimOptionsLoading,
} from '../store/policySlice';

function* getCreatePolicyOptionsSaga({ payload }: PayloadAction<string>) {
  try {
    const { data: policyClaimOptions } = yield call(
      HttpService.get,
      `/examiner/claim-sets/${payload}/claims-refactored-not-linked`,
    );

    const combinedData = {
      claimIdList: policyClaimOptions ?? [],
    };

    yield put(getCreatePolicyOptionsSuccess(combinedData));
  } catch (error) {
    console.error(error);
  }
}

function* addNewPolicySaga({ payload }: PayloadAction<AddNewPolicyBodyRequest>) {
  try {
    const claimSetId = yield select((state) => state.claimSets.currentClaimSetId);
    yield call(HttpService.post, '/examiner/policies/create-potential-policy', payload);

    yield put(getPotentialPoliciesFilteredList());
    yield put(addNewPolicySuccess());

    SnacksService.add({
      type: SnackType.Success,
      title: `Policy ${payload.policyNumber} has been added successfully`,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });

    if (claimSetId) yield put(getClaimSetV2(String(claimSetId)));

    ModalService.close(Modals.AddPotentialPolicy);

    EventService.dispatch(new PolicyEvents.PolicyCreated({}));
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save update, please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
    ModalService.close(Modals.AddPotentialPolicy);
  }
}

function* getClaimOptionsSaga({ payload }: PayloadAction<string>) {
  try {
    yield put(setClaimOptionsLoading(true));

    const { data } = yield call(
      HttpService.get,
      `/v2/ClaimSet/${payload}?includeEntities=ClaimEntities`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data);

    const claimOptions = (normalizedResponse.entities.ClaimEntities as NormalizedV2Response[]).map((value) => ({
      label: value.fields.claimNumber?.value?.toString() || '',
      value: value.id.toString(),
      hasLinkedPolicy: !!value.fields.policyId?.value,
    }));

    if (data) {
      yield put(getClaimOptionsSuccess(claimOptions));
    }
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to load claim options.',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setClaimOptionsLoading(false));
  }
}

function* linkPolicyToClaimSaga({ payload }: PayloadAction<LinkPolicyToClaimRequest>) {
  try {
    yield put(setSystemLoadingStatus(true));
    yield call(
      HttpService.post,
      `/examiner/policies/link-to-claim?policyId=${payload.policyId}&claimId=${payload.claimId}`,
      {},
    );

    yield put(getBenefitSectionDetails(payload.claimId));

    SnacksService.add({
      type: SnackType.Success,
      title: `Policy ${payload.policyNumber} successfully linked to Claim ${payload.claimNumber}.`,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the update.',
      content: 'Please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
    ModalService.close(Modals.LinkPolicy);
  }
}

function* deletePotentialPolicySaga({ payload: { policyId, policyNumber } }: PayloadAction<DeletePolicyBodyRequest>) {
  try {
    const claimSetId = yield select((state) => state.claimSets.currentClaimSetId);
    yield put(setSystemLoadingStatus(true));

    yield call(HttpService.delete, `/examiner/policies/potential-policy/${policyId}`);

    yield put(getPotentialPoliciesFilteredList());

    SnacksService.add({
      type: SnackType.Success,
      title: `Policy ${policyNumber} has been deleted successfully`,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });

    if (claimSetId) yield put(getClaimSetV2(String(claimSetId)));
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: FAILED_TO_SAVE,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

export function* addPolicyWatcher() {
  yield takeLatest(getPolicyStatuses.type, getCreatePolicyOptionsSaga);
  yield takeLatest(linkPolicyToClaim.type, linkPolicyToClaimSaga);
  yield takeLatest(addNewPolicy.type, addNewPolicySaga);
  yield takeLatest(getClaimOptions.type, getClaimOptionsSaga);
  yield takeLatest(deletePolicy.type, deletePotentialPolicySaga);
}
